import { useState, useCallback, useEffect } from 'react';
import { api, type CalendarEvent } from '../api/client';
import { usePolling } from '../hooks/usePolling';

function formatTime(unix: number) {
  return new Date(unix * 1000).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}
function formatDateShort(unix: number) {
  return new Date(unix * 1000).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
}
function isToday(unix: number) {
  const d = new Date(unix * 1000), now = new Date();
  return d.toDateString() === now.toDateString();
}

export default function CalendarScreen() {
  const [today, setToday] = useState<CalendarEvent[]>([]);
  const [upcoming, setUpcoming] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const digest = await api.digest();
      setToday(digest.calendar.today);
      setUpcoming(digest.calendar.upcoming);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  usePolling(load);

  const EventRow = ({ event }: { event: CalendarEvent }) => {
    const now = Date.now() / 1000;
    const isLive = event.startAt <= now && event.endAt >= now;
    const isPast = event.endAt < now;
    return (
      <div className="event-row" style={{ opacity: isPast ? 0.45 : 1 }}>
        <div className="event-time">
          {event.isAllDay ? 'ALL DAY' : formatTime(event.startAt)}
        </div>
        <div className={`event-accent ${isLive ? 'live' : ''}`} />
        <div className="event-info">
          <div className="event-title">{event.title}</div>
          {!isToday(event.startAt) && (
            <div className="event-meta" style={{ color: 'var(--primary)', fontWeight: 500 }}>
              {formatDateShort(event.startAt)}
            </div>
          )}
          {isLive && <span className="live-chip">● LIVE NOW</span>}
          {event.location && <div className="event-meta">📍 {event.location}</div>}
          {event.attendeeCount > 0 && <div className="event-meta">👥 {event.attendeeCount} attendees</div>}
        </div>
      </div>
    );
  };

  return (
    <div className="main-content">
      <h1 style={{ fontSize: 22, fontWeight: 800 }}>📅 Schedule</h1>
      {loading ? (
        <div className="card"><p style={{ color: 'var(--text-2)' }}>Loading…</p></div>
      ) : (
        <>
          <div className="card">
            <div className="section-heading">Today</div>
            {today.length === 0
              ? <div className="empty-state"><div className="empty-state-emoji">🎉</div><p className="empty-state-text">Free day!</p></div>
              : today.map((e) => <EventRow key={e.id} event={e} />)
            }
          </div>
          {upcoming.length > 0 && (
            <div className="card">
              <div className="section-heading">Upcoming</div>
              {upcoming.map((e) => <EventRow key={e.id} event={e} />)}
            </div>
          )}
        </>
      )}
    </div>
  );
}
